import { useCallback } from "react";
import { type MutationHookOptions } from "@apollo/client";
import { SpacesDocument, useDeleteSpaceLocalAttributeMutation } from "#graphql/supergraphHooks";
import {
  type DeleteSpaceLocalAttributeMutation,
  type DeleteSpaceLocalAttributeMutationVariables,
} from "#graphql/supergraphTypes";

export interface UseDeleteSpaceLocalAttributeReturn {
  deleteSpaceLocalAttribute: (
    attributeId: string,
  ) => Promise<DeleteSpaceLocalAttributeMutation["deleteSpaceLocalAttribute"] | null>;
  loading: boolean;
  error: ReturnType<typeof useDeleteSpaceLocalAttributeMutation>[1]["error"];
}

/** Soft-delete a space-local attribute and refresh its owning Space catalog. */
export const useDeleteSpaceLocalAttribute = (
  options?: MutationHookOptions<
    DeleteSpaceLocalAttributeMutation,
    DeleteSpaceLocalAttributeMutationVariables
  >,
): UseDeleteSpaceLocalAttributeReturn => {
  const [mutate, { loading, error }] = useDeleteSpaceLocalAttributeMutation({
    refetchQueries: [SpacesDocument],
    awaitRefetchQueries: true,
    ...options,
  });

  const deleteSpaceLocalAttribute = useCallback(
    async (attributeId: string) => {
      const { data } = await mutate({ variables: { id: attributeId } });
      return data?.deleteSpaceLocalAttribute ?? null;
    },
    [mutate],
  );

  return { deleteSpaceLocalAttribute, loading, error };
};
